import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PrivatePath } from './privatePaths';

/**
 * document.title for the signed-in application pages.
 *
 * Only those pages: every marketing and content page renders <PageSeo>, which owns
 * its <title> through react-helmet-async. Both used to write it — this hook for the
 * paths it listed and "Localingos" for everything else — so the title a page ended
 * up with was decided by whichever effect happened to run last. In build/prod that
 * cost 16 of the 20 English pages their real <title>, including the homepage
 * (`Localingos`) and /docs (`docs.page_title | Localingos`, an unresolved key,
 * because the marketing entries here were never kept in step with the catalogue).
 *
 * Typed as a total Record over PrivatePath so a new signed-in page cannot be added
 * without giving it a title, and so this list cannot drift back into the marketing
 * routes.
 */
const APP_PAGE_TITLE_KEYS: Record<PrivatePath, string> = {
    '/dashboard': 'nav.dashboard',
    '/families': 'nav.families',
    '/translatables': 'nav.translatables',
    '/translations': 'nav.translations',
    '/translation-jobs': 'nav.translation_jobs',
    '/billing': 'nav.billing',
    '/api-keys': 'nav.api_keys',
    '/admin/users': 'nav.user_management',
};

export function usePageTitle() {
    const { pathname } = useLocation();
    const { t } = useTranslation();

    useEffect(() => {
        const key = APP_PAGE_TITLE_KEYS[pathname as PrivatePath];
        // Anything else is left exactly as it is: on a marketing page that is the
        // title <PageSeo> just set, and overwriting it here is how those pages ended
        // up serving the wrong one to crawlers and to anyone sharing a link.
        if (!key) return;
        document.title = `${t(key)} | Localingos`;
    }, [pathname, t]);
}
